import React from 'react';
import { content, SupportedLocale, localizeBlogPost } from '../lib/content';

type BlogSectionProps = {
  locale?: SupportedLocale;
};

export const BlogSection: React.FC<BlogSectionProps> = ({ locale = 'pl' }) => {
  const { teaser, posts } = content[locale].blog;
  const featuredPosts = posts.filter((post) =>
    teaser.featuredSlugs.includes(post.slug)
  );

  const categoryLabels: Record<SupportedLocale, Record<string, string>> = {
    pl: {
      'Paragraf Zwierzoochronny': 'Paragraf Zwierzoochronny',
      'Poradnik klienta': 'Poradnik klienta',
      'Legalizacja pobytu': 'Legalizacja pobytu',
      'Franki, SKD, WIBOR': 'Franki, SKD, WIBOR',
    },
    en: {
      'Paragraf Zwierzoochronny': 'Animal rights',
      'Poradnik klienta': 'Client guide',
      'Legalizacja pobytu': 'Legalisation of stay',
      'Franki, SKD, WIBOR': 'Finance and loans',
    },
    es: {
      'Paragraf Zwierzoochronny': 'Derechos de los animales',
      'Poradnik klienta': 'Guía para clientes',
      'Legalizacja pobytu': 'Legalización de la estancia',
      'Franki, SKD, WIBOR': 'Finanzas y créditos',
    },
  };

  const monthMap: Record<string, number> = {
    stycznia: 0,
    lutego: 1,
    marca: 2,
    kwietnia: 3,
    maja: 4,
    czerwca: 5,
    lipca: 6,
    sierpnia: 7,
    września: 8,
    października: 9,
    listopada: 10,
    grudnia: 11,
  };

  const monthNamesEn = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const monthNamesEs = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];

  const formatDate = (dateStr: string): string => {
    if (locale === 'pl') return dateStr;
    const parts = dateStr.split(' ').filter(Boolean);
    if (parts.length < 3) return dateStr;
    const [dayStr, monthStr, yearStr] = parts;
    const day = parseInt(dayStr, 10);
    const monthIndex = monthMap[monthStr.toLowerCase()];
    const year = parseInt(yearStr, 10);
    if (
      Number.isNaN(day) ||
      Number.isNaN(year) ||
      monthIndex === undefined ||
      monthIndex < 0 ||
      monthIndex > 11
    ) {
      return dateStr;
    }
    if (locale === 'en') {
      return `${day} ${monthNamesEn[monthIndex]} ${year}`;
    }
    // es
    return `${day} de ${monthNamesEs[monthIndex]} de ${year}`;
  };

  const formatReadingTime = (readingTime: string): string => {
    if (locale === 'pl') return readingTime;
    const match = readingTime.match(/(\d+)/);
    const minutes = match ? match[1] : '';
    if (!minutes) {
      return locale === 'en' ? 'Few minutes read' : 'Unos minutos de lectura';
    }
    if (locale === 'en') {
      return `${minutes} min read`;
    }
    // es
    return `${minutes} min de lectura`;
  };

  const readLabel =
    locale === 'en' ? 'Read' : locale === 'es' ? 'Leer' : 'Czytaj';

  return (
    <section id={teaser.id} className="blog-section reveal">
      <div className="container">
        <div className="section-heading">
          <h2>{teaser.title}</h2>
        </div>
        <p className="section-intro">{teaser.subtitle}</p>
        <div className="blog-tiles grid-cols-2">
          {featuredPosts.map((post) => (
            <article className="card blog-card" key={post.slug}>
              {(() => {
                const localized = localizeBlogPost(post, locale);
                return (
                  <>
                    <div className="blog-meta">
                      <span className="badge">
                        {categoryLabels[locale][post.category] ?? post.category}
                      </span>
                      <span className="muted blog-meta-detail">
                        {formatDate(post.date)} • {formatReadingTime(post.readingTime)}
                      </span>
                    </div>
                    <h3>{localized.title}</h3>
                    <p>{localized.teaser}</p>
                    <a className="inline-link with-icon" href={post.link}>
                      {readLabel}
                      <span aria-hidden="true">→</span>
                    </a>
                  </>
                );
              })()}
            </article>
          ))}
        </div>
        <div className="blog-cta">
          <a className="btn btn-outline" href={teaser.ctaLink}>
            {teaser.ctaLabel}
          </a>
        </div>
      </div>
    </section>
  );
};
